import { clientLogger } from '@/lib/clientLogger';
import { useState, useCallback, useRef, useMemo, DragEvent, ChangeEvent, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { compressImage } from '@/lib/imageCompression';
import { PrepareUploadResponse } from '@/types';
import { X, Image, Download, Upload, Camera, Trash2, Loader2, FileText, Pencil, Check, Plus } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ImagePreviewModal } from '@/components/ui/ImagePreviewModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { destructiveCopy } from '@/lib/destructiveConfirmMessages';
import { useIsMobile } from '@/hooks/useIsMobile';
import { openDownloadUrl, isNative } from '@/lib/capacitor';
import { createPortal } from 'react-dom';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';

// Common attachment shape that both job attachments and expense attachments share
export interface AttachmentItem {
  id: string;
  attachment_id: string;
  title: string | null;
  object_key: string;
  mime_type: string;
  download_url: string;
  /** Job/estimate attachments only: appended to the invoice/estimate PDF. */
  include_in_pdf?: boolean;
  created_at: string;
}

/** Turns on the "include in PDF" toggle on each attachment (job and estimate attachments only). */
export interface AttachmentsPdfOptions {
  /** Which PDF the toggle adds to: shown as "In invoice PDF" once included. */
  document: 'invoice' | 'estimate';
  setIncluded: (ownerId: string, attachmentId: string, included: boolean) => Promise<unknown>;
  /** New uploads are ticked straight away (opened from the invoice/estimate). */
  uploadsIncluded?: boolean;
}

export interface AttachmentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Id of the job/estimate the attachments belong to; passed straight to the API functions. */
  ownerId: string;
  title?: string;
  /** View and download only: hides upload, rename and delete (e.g. a converted estimate). */
  readOnly?: boolean;
  onAttachmentsUpdated: () => void;
  // API functions injected as props
  fetchAttachments: (ownerId: string) => Promise<AttachmentItem[]>;
  prepareUpload: (ownerId: string, count: number, fileNames?: string[]) => Promise<PrepareUploadResponse[]>;
  completeUpload: (ownerId: string, uploads: any[]) => Promise<any>;
  getDownloadUrl: (ownerId: string, attachmentId: string) => Promise<{ download_url: string }>;
  updateAttachment: (ownerId: string, attachmentId: string, data: { title: string }) => Promise<any>;
  deleteAttachment: (ownerId: string, attachmentId: string) => Promise<any>;
  // Extra fields to merge into each complete-upload item (e.g. job_expense_id)
  extraUploadFields?: Record<string, any>;
  queryKey: string;
  pdf?: AttachmentsPdfOptions;
}

interface SelectedFileWithTitle {
  file: File;
  title: string;
  id: string;
}

const FilePreview = ({ file }: { file: File }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!previewUrl) return <div className="w-16 h-16 bg-gray-100 animate-pulse rounded border" />;

  return (
    <img
      src={previewUrl}
      alt={file.name}
      className="w-16 h-16 object-cover rounded border"
    />
  );
};

export function AttachmentsModal({
  isOpen,
  onClose,
  ownerId,
  title: modalTitle = 'Attachments',
  readOnly = false,
  onAttachmentsUpdated,
  fetchAttachments,
  prepareUpload,
  completeUpload,
  getDownloadUrl,
  updateAttachment,
  deleteAttachment,
  extraUploadFields,
  queryKey,
  pdf,
}: AttachmentsModalProps) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // States
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileWithTitle[]>([]);
  const [compressedFiles, setCompressedFiles] = useState<Array<{
    file: File;
    blob: Blob;
    mimeType: string;
    sizeBytes: number;
    title: string;
  }>>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState<PrepareUploadResponse[]>([]);
  const [galleryAttachments, setGalleryAttachments] = useState<AttachmentItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);

  // Edit title state
  const [editingAttachment, setEditingAttachment] = useState<AttachmentItem | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Delete state
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [attachmentPendingDelete, setAttachmentPendingDelete] = useState<AttachmentItem | null>(null);

  // Preview state
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const layer = useModalLayer({
    enabled: isOpen && !editingAttachment && !deletingAttachmentId && !attachmentPendingDelete,
    onClose: () => {
      setSelectedFiles([]);
      setUploadingAttachments([]);
      setUploadProgress({});
      setGalleryAttachments([]);
      setError(null);
      setIsUploading(false);
      setEditingAttachment(null);
      setPreviewIndex(null);
      onClose();
    },
  });

  // Load existing attachments
  const { data: initialAttachments, isLoading: loadingAttachments, refetch } = useQuery({
    queryKey: [queryKey, ownerId],
    queryFn: () => fetchAttachments(ownerId),
    enabled: isOpen,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (isOpen && initialAttachments) {
      setGalleryAttachments(initialAttachments);
    }
  }, [initialAttachments, isOpen]);

  // Prepare upload mutation
  const prepareUploadMutation = useMutation({
    mutationFn: (params: { attachmentCount: number; fileNames: string[] }) =>
      prepareUpload(ownerId, params.attachmentCount, params.fileNames),
    onSuccess: (responses) => {
      setUploadingAttachments(responses);
      uploadAttachments(responses);
    },
    onError: (error: any) => {
      setError('Failed to prepare upload. Please try again.');
      clientLogger.error('Prepare upload error:', error);
    },
  });

  // Complete upload mutation
  const completeUploadMutation = useMutation({
    mutationFn: (uploads: any[]) => completeUpload(ownerId, uploads),
    onSuccess: () => {
      setIsUploading(false);
      setSelectedFiles([]);
      setUploadingAttachments([]);
      setUploadProgress({});
      queryClient.invalidateQueries({ queryKey: [queryKey, ownerId], refetchType: 'all' });
      onAttachmentsUpdated();
    },
    onError: (error: any) => {
      setError('Upload completed but registration failed. Please refresh the page.');
      clientLogger.error('Complete upload error:', error);
    },
  });

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const MAX_PDF_BYTES = 10 * 1024 * 1024; // 10MB

    const newFiles = Array.from(files).filter(file => {
      if (file.type === 'application/pdf') {
        if (file.size > MAX_PDF_BYTES) {
          setError('PDF size too large. Please select PDFs under 10MB');
          return false;
        }
        return true;
      }

      if (!file.type.startsWith('image/')) {
        setError('Only image or PDF files are allowed');
        return false;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError('File size too large. Please select images under 10MB');
        return false;
      }
      return true;
    });

    if (newFiles.length + selectedFiles.length > 10) {
      setError('Maximum 10 attachments per upload');
      return;
    }

    const filesWithTitle: SelectedFileWithTitle[] = newFiles.map(file => ({
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));

    setSelectedFiles(prev => [...prev, ...filesWithTitle]);
    setError(null);
  }, [selectedFiles.length]);

  const updateFileTitle = useCallback((id: string, newTitle: string) => {
    setSelectedFiles(prev => prev.map(f => f.id === id ? { ...f, title: newTitle } : f));
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleGallerySelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    e.target.value = '';
  }, [handleFileSelect]);

  const handleCameraCapture = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    e.target.value = '';
  }, [handleFileSelect]);

  const removeFile = useCallback((id: string) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const startUpload = useCallback(async () => {
    if (selectedFiles.length === 0) {
      setError('No attachments selected');
      return;
    }

    setIsCompressing(true);
    setError(null);

    try {
      const compressedFilesLocal: Array<{
        file: File;
        blob: Blob;
        mimeType: string;
        sizeBytes: number;
        title: string;
      }> = [];

      for (const sf of selectedFiles) {
        try {
          const compressed = await compressImage(sf.file);
          compressedFilesLocal.push({
            file: sf.file,
            blob: compressed.blob,
            mimeType: compressed.mimeType,
            sizeBytes: compressed.sizeBytes,
            title: sf.title
          });
        } catch (error) {
          clientLogger.error('Compression error:', error);
          setError(`Failed to compress ${sf.file.name}. Please try a different file.`);
          return;
        }
      }

      setCompressedFiles(compressedFilesLocal);

      const fileNames = compressedFilesLocal.map(({ file }) => file.name);
      prepareUploadMutation.mutate({ attachmentCount: compressedFilesLocal.length, fileNames });
    } catch (error) {
      setError('Failed to prepare attachments for upload');
      clientLogger.error('Upload preparation error:', error);
    } finally {
      setIsCompressing(false);
    }
  }, [selectedFiles, prepareUploadMutation]);

  const uploadAttachments = useCallback(async (presigned: PrepareUploadResponse[]) => {
    setIsUploading(true);
    const successfulUploads: any[] = [];

    for (let i = 0; i < presigned.length; i++) {
      const { upload_url, fields, attachment_id, object_key } = presigned[i];
      const compressed = compressedFiles[i];

      try {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', compressed.blob, compressed.file.name);

        const response = await fetch(upload_url, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          successfulUploads.push({
            attachment_id,
            object_key,
            mime_type: compressed.mimeType,
            size_bytes: compressed.sizeBytes,
            title: compressed.title,
            ...extraUploadFields,
            ...(pdf?.uploadsIncluded ? { include_in_pdf: true } : {}),
          });

          setUploadProgress(prev => ({
            ...prev,
            [attachment_id]: 100
          }));
        } else {
          throw new Error(`Upload failed for ${compressed.file.name}`);
        }
      } catch (error) {
        clientLogger.error(`Upload error for ${compressed.file.name}:`, error);
        setUploadProgress(prev => ({
          ...prev,
          [attachment_id]: 0
        }));
      }
    }

    if (successfulUploads.length > 0) {
      completeUploadMutation.mutate(successfulUploads);
    } else {
      setError('No attachments were successfully uploaded');
      setIsUploading(false);
    }
  }, [completeUploadMutation, compressedFiles, extraUploadFields, pdf?.uploadsIncluded]);

  const imageAttachments = useMemo(
    () => galleryAttachments.filter((a) => a.mime_type?.startsWith('image/')),
    [galleryAttachments],
  );

  const handleAttachmentClick = useCallback(async (attachment: AttachmentItem) => {
    if (attachment.mime_type?.startsWith('image/')) {
      const idx = imageAttachments.findIndex((a) => a.id === attachment.id);
      if (idx !== -1) {
        setPreviewIndex(idx);
        return;
      }
    }
    try {
      const data = await getDownloadUrl(ownerId, attachment.attachment_id);
      await openDownloadUrl(data.download_url);
    } catch (error) {
      setError('Failed to generate download URL');
      clientLogger.error('Download URL error:', error);
    }
  }, [ownerId, getDownloadUrl, imageAttachments]);

  const downloadAttachment = useCallback(async (attachment: AttachmentItem) => {
    try {
      const data = await getDownloadUrl(ownerId, attachment.attachment_id);
      if (isNative()) {
        await openDownloadUrl(data.download_url);
        return;
      }
      const resp = await fetch(data.download_url);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.title || attachment.attachment_id;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      setError('Failed to download file');
      clientLogger.error('Download error:', error);
    }
  }, [ownerId, getDownloadUrl]);

  const handlePreviewDownload = useCallback(async () => {
    if (previewIndex === null) return;
    const attachment = imageAttachments[previewIndex];
    if (!attachment) return;
    setIsDownloading(true);
    try {
      await downloadAttachment(attachment);
    } finally {
      setIsDownloading(false);
    }
  }, [previewIndex, imageAttachments, downloadAttachment]);

  // Edit title handlers
  const openEditModal = useCallback((attachment: AttachmentItem) => {
    setEditingAttachment(attachment);
    setEditingTitle(attachment.title || '');
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingAttachment(null);
    setEditingTitle('');
  }, []);

  const saveTitle = useCallback(async () => {
    if (!editingAttachment) return;
    setIsSavingTitle(true);
    try {
      await updateAttachment(ownerId, editingAttachment.id, { title: editingTitle });
      setEditingAttachment(null);
      refetch();
    } catch (err) {
      setError('Failed to update title');
    } finally {
      setIsSavingTitle(false);
    }
  }, [editingAttachment, editingTitle, ownerId, refetch, updateAttachment]);

  // Delete handler
  const runDeleteAttachment = useCallback(async (attachment: AttachmentItem) => {
    setDeletingAttachmentId(attachment.id);
    try {
      await deleteAttachment(ownerId, attachment.id);
      refetch();
      onAttachmentsUpdated();
    } catch (err) {
      setError('Failed to delete attachment');
      throw err;
    } finally {
      setDeletingAttachmentId(null);
    }
  }, [ownerId, refetch, onAttachmentsUpdated, deleteAttachment]);

  // Optimistic: flip the tile now, roll back if the save fails.
  const togglePdf = useCallback(async (attachment: AttachmentItem) => {
    if (!pdf) return;
    const included = !attachment.include_in_pdf;
    const setLocal = (value: boolean) =>
      setGalleryAttachments(prev => prev.map(a => a.id === attachment.id ? { ...a, include_in_pdf: value } : a));
    setLocal(included);
    try {
      await pdf.setIncluded(ownerId, attachment.id, included);
      queryClient.invalidateQueries({ queryKey: [queryKey, ownerId] });
    } catch (err) {
      setLocal(!included);
      setError('Failed to update the PDF selection');
      clientLogger.error('Toggle include-in-PDF error:', err);
    }
  }, [pdf, ownerId, queryClient, queryKey]);

  const handleClose = useCallback(() => {
    layer.onClose();
  }, [layer]);

  if (!isOpen) return null;

  const totalSelected = selectedFiles.length;
  const totalUploading = uploadingAttachments.length;
  const totalGallery = galleryAttachments.length;
  const totalInPdf = galleryAttachments.filter(a => a.include_in_pdf).length;

  return createPortal(
    <LayerDepthContext.Provider value={layer.depth}>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={layer.style}>
      <div className={`bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col ${isMobile ? 'h-full max-w-full' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold">{modalTitle} ({totalGallery})</h2>
            {pdf && <p className="text-sm text-gray-500">{totalInPdf} added to the PDF</p>}
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Upload Section */}
          {!readOnly && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-4">Upload Attachments</h3>
            {pdf?.uploadsIncluded && (
              <p className="text-sm text-gray-500 -mt-3 mb-4">New uploads are added to the PDF and saved to the attachments.</p>
            )}

            {totalSelected === 0 && !isUploading && (
              <div className="space-y-4">
                {isMobile ? (
                  <>
                    {/* Mobile: Camera and Gallery buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => cameraInputRef.current?.click()}
                        className="flex flex-col items-center p-4 border-2 border-dashed rounded-lg hover:border-blue-400 transition-colors"
                      >
                        <Camera className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm font-medium text-gray-700">Take Photo</span>
                      </button>
                      <button
                        onClick={() => galleryInputRef.current?.click()}
                        className="flex flex-col items-center p-4 border-2 border-dashed rounded-lg hover:border-blue-400 transition-colors"
                      >
                        <Image className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm font-medium text-gray-700">Choose from Gallery</span>
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Up to 10 attachments (JPEG/PNG compressed under 250KB, PDFs under 10MB)
                    </p>
                  </>
                ) : (
                  // Desktop: Drag-drop zone
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => galleryInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <Camera className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg text-gray-600">Drop attachments here or click to select</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Up to 10 attachments (JPEG/PNG compressed under 250KB, PDFs under 10MB)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Selected Files List with Title Input */}
            {totalSelected > 0 && !isUploading && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">{totalSelected} attachments selected</h4>
                  <div className="flex gap-2">
                    <button onClick={() => galleryInputRef.current?.click()} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Add More</button>
                    <button
                      onClick={startUpload}
                      className="btn-primary flex items-center gap-2"
                      disabled={isCompressing}
                    >
                      {isCompressing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Compressing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4" />
                          Upload All
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {selectedFiles.map(({ file, title, id }) => {
                    const isImage = file.type.startsWith('image/');
                    return (
                      <div key={id} className="flex items-start gap-3 p-3 border rounded-lg bg-gray-50">
                        <div className="flex-shrink-0">{isImage ? <FilePreview file={file} /> : <div className="w-16 h-16 rounded flex items-center justify-center bg-white border"><FileText className="h-6 w-6 text-gray-500" /></div>}</div>
                        <div className="flex-1 min-w-0">
                          <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
                          <input type="text" value={title} onChange={(e) => updateFileTitle(id, e.target.value)} className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500" placeholder="Enter title..." />
                          <p className="text-xs text-gray-400 mt-1 truncate">{file.name} • {(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button onClick={() => removeFile(id)} className="flex-shrink-0 p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {totalUploading > 0 && isUploading && (
              <div className="space-y-4">
                <h4 className="font-medium">Uploading {totalUploading} attachments...</h4>
                <div className="space-y-2">
                  {uploadingAttachments.map((upload) => (
                    <div key={upload.attachment_id} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between text-sm mb-1">
                          <span>{upload.attachment_id}</span>
                          <span>{uploadProgress[upload.attachment_id] || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${uploadProgress[upload.attachment_id] || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}

          {/* Hidden inputs */}
          <input
            ref={galleryInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf"
            onChange={handleGallerySelect}
            className="hidden"
          />
          {isMobile && (
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraCapture}
              className="hidden"
            />
          )}

          {/* Gallery Section */}
          <div>
            <h3 className="text-lg font-medium mb-4">Gallery ({totalGallery} attachments)</h3>
            {loadingAttachments ? (
              <LoadingSpinner />
            ) : totalGallery === 0 ? (
              <p className="text-gray-500 text-center py-8">No attachments uploaded yet</p>
            ) : (
              <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-2`}>
                {galleryAttachments.map((attachment) => {
                  const isImage = attachment.mime_type?.startsWith('image/');
                  const isDeleting = deletingAttachmentId === attachment.id;
                  const inPdf = !!pdf && !!attachment.include_in_pdf;
                  return (
                    <div key={attachment.id} className={`relative group ${isDeleting ? 'opacity-50' : ''}`}>
                      <div
                        className={`w-full aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer ${inPdf ? 'ring-[3px] ring-blue-600 ring-offset-2' : ''}`}
                        onClick={() => handleAttachmentClick(attachment)}
                      >
                        {isImage ? (
                          <img
                            src={`${attachment.download_url}`}
                            alt={attachment.title || attachment.attachment_id}
                            className="w-full h-full object-cover"
                            // Grid of full-size attachments: without this every photo on the job
                            // downloads the moment the modal opens. Safe from layout shift because
                            // the wrapper is aspect-square, so the cell reserves its space anyway.
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjRjNGNEY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iI0JCMkIyQSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4=';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-white">
                            <FileText className="h-10 w-10 text-gray-500" />
                          </div>
                        )}
                      </div>
                      {inPdf && (
                        <span className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white shadow pointer-events-none">
                          <Check className="h-3 w-3" strokeWidth={3} />
                          PDF
                        </span>
                      )}
                      {/* Hover overlay with action buttons */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all rounded-lg pointer-events-none" />
                      <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-all">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadAttachment(attachment);
                          }}
                          className="p-1.5 rounded-full bg-white/90 hover:bg-white shadow-sm"
                          title="Download"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        {!readOnly && (<>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(attachment);
                          }}
                          className="p-1.5 rounded-full bg-white/90 hover:bg-white shadow-sm"
                          title="Edit title"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAttachmentPendingDelete(attachment);
                          }}
                          className="p-1.5 rounded-full bg-white/90 hover:bg-white shadow-sm text-red-500"
                          title="Delete"
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                        </>)}
                      </div>
                      <p className="text-xs mt-1 truncate font-medium">{attachment.title || 'Untitled'}</p>
                      {pdf && (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={inPdf}
                          disabled={readOnly || isDeleting}
                          onClick={() => togglePdf(attachment)}
                          className={`mt-1.5 flex w-full min-h-[40px] items-center justify-center gap-1.5 rounded-lg border-2 px-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            inPdf
                              ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
                              : 'border-dashed border-blue-400 bg-white text-blue-700 hover:bg-blue-50'
                          }`}
                        >
                          {inPdf ? <Check className="h-4 w-4 shrink-0" strokeWidth={3} /> : <Plus className="h-4 w-4 shrink-0" />}
                          <span className="truncate">{inPdf ? `In ${pdf.document} PDF` : 'Include in PDF'}</span>
                        </button>
                      )}
                      {attachment.created_at && (
                        <p className="text-[11px] text-gray-500">
                          {new Date(attachment.created_at).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Title Modal */}
      {editingAttachment && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" onClick={closeEditModal}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Edit Title</h3>
            <input
              type="text"
              value={editingTitle}
              onChange={(e) => setEditingTitle(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-4"
              placeholder="Attachment title"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveTitle();
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                disabled={isSavingTitle}
              >
                Cancel
              </button>
              <button
                onClick={saveTitle}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={isSavingTitle || !editingTitle.trim()}
              >
                {isSavingTitle ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!attachmentPendingDelete}
        onClose={() => setAttachmentPendingDelete(null)}
        onConfirm={async () => {
          if (attachmentPendingDelete) {
            await runDeleteAttachment(attachmentPendingDelete);
          }
        }}
        isLoading={!!deletingAttachmentId}
        title="Delete attachment?"
        message={
          <div className="space-y-2">
            <span>{destructiveCopy.deleteAttachment}</span>
            <span className="block text-sm">{destructiveCopy.cannotUndo}</span>
          </div>
        }
        confirmText="Delete"
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        imageUrl={previewIndex !== null ? imageAttachments[previewIndex]?.download_url ?? '' : ''}
        title={previewIndex !== null ? imageAttachments[previewIndex]?.title ?? undefined : undefined}
        onDownload={handlePreviewDownload}
        isDownloading={isDownloading}
        onPrev={previewIndex !== null && previewIndex > 0 ? () => setPreviewIndex((i) => (i !== null ? i - 1 : null)) : undefined}
        onNext={previewIndex !== null && previewIndex < imageAttachments.length - 1 ? () => setPreviewIndex((i) => (i !== null ? i + 1 : null)) : undefined}
      />
    </div>
    </LayerDepthContext.Provider>,
    document.body
  );
}
